import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { getFlagURL, getFullname, prettyFloatMoney } from '@/helpers/utils';
import { prettyDate } from '@/helpers/date';
import { DATE_FORMAT_YYYY } from '@/helpers/common';
import { ReadMoreLess } from '../../components/ReadMoreLess';
import { BrandSocials } from '../../components/BrandSocials';
import { BrandGallery } from '../../components/BrandGallery';
import { BrandFounders } from '../../components/BrandFounders';
import s from './index.module.scss';

export const BrandDetail = ({ brand }) => {
  const { t } = useTranslation('brands');
  const foundersDescription = useMemo(() => {
    if (brand.co_founders.length) {
      return brand.co_founders
        .map(f => [getFullname(f.first_name, f.last_name), f.description].join('\n'))
        .join('\n\n');
    }
    return '';
  }, [brand.co_founders]);

  return (
    <div>
      <div className={s.header}>
        <div className={s.image}>
          {brand.promo_image && (
            <img src={brand.promo_image?.file} alt={brand.name} width={235} height={235} />
          )}
        </div>
        <div className={s.right}>
          <div className={s.group}>
            <h1 className="f-hero-h3">{brand.name}</h1>
            <BrandSocials
              className={s.socials}
              web={brand.site}
              linkedin={brand.linkedin}
              facebook={brand.facebook}
              instagram={brand.instagram}
            />
          </div>
          <div className={s.badges}>
            <div className={classnames(s.badge, s.country)}>
              <div className={s.country_image}>
                <img
                  src={getFlagURL(brand.country_code)}
                  alt={brand.country}
                  width={16}
                  height={16}
                />
              </div>
              <span className="f-tiny-accent">{brand.country}</span>
            </div>
            {brand.foundation_date && (
              <div className={classnames(s.badge, s.year, 'f-tiny-accent')}>
                {t('brand_detail.foundation', {
                  date: prettyDate(brand.foundation_date, DATE_FORMAT_YYYY),
                })}
              </div>
            )}
          </div>
          <div className={s.stats}>
            <div className={s.stats_group}>
              <div className="f-body-regular-s">{prettyFloatMoney(brand.total_loans)}</div>
              <div className="f-body-regular">{t('brand_detail.total_loans')}</div>
            </div>
            <div className={s.stats_group}>
              <div className="f-body-regular-s">{prettyFloatMoney(brand.current_balance)}</div>
              <div className="f-body-regular">{t('brand_detail.current_balance')}</div>
            </div>
            <div className={s.stats_group}>
              <div className="f-body-regular-s">
                {brand.time_on_platform <= 0
                  ? t('brand_detail.less_than_month')
                  : t(brand.time_on_platform === 1 ? 'brand_detail.month' : 'brand_detail.months', {
                      count: brand.time_on_platform,
                    })}
              </div>
              <div className="f-body-regular">{t('brand_detail.time_on_platform')}</div>
            </div>
            <div className={s.stats_group}>
              <div className="f-body-regular-s">{brand.round_participation}</div>
              <div className="f-body-regular">{t('brand_detail.round_participation')}</div>
            </div>
          </div>
        </div>
      </div>
      <div className={s.paragraph}>
        <h1 className={s.subtitle}>{t('brand_detail.about_company')}</h1>
        <ReadMoreLess text={brand.description} rows={5} className="f-body-regular-s" />
      </div>
      <div className={s.paragraph}>
        <h1 className={s.subtitle}>{t('brand_detail.about_founders')}</h1>
        <ReadMoreLess text={foundersDescription} rows={5} className="f-body-regular-s" />
      </div>
      <BrandGallery images={brand.images} />
      <BrandFounders founders={brand.co_founders} />
    </div>
  );
};
